import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../Config';
import { FaTimes, FaCoins, FaArrowLeft, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const FLUENT = 'https://cdn.jsdelivr.net/gh/microsoft/fluentui-emoji@main/assets';

export const GIFTS = [
  { id: 'cookie',    name: 'Galleta',   coins: 1,    emoji: '🍪', img: `${FLUENT}/Cookie/3D/cookie_3d.png` },
  { id: 'rose',      name: 'Rosa',      coins: 5,    emoji: '🌹', img: `${FLUENT}/Rose/3D/rose_3d.png` },
  { id: 'heart',     name: 'Corazón',   coins: 10,   emoji: '💖', img: `${FLUENT}/Sparkling heart/3D/sparkling_heart_3d.png` },
  { id: 'kiss',      name: 'Beso',      coins: 20,   emoji: '💋', img: `${FLUENT}/Kiss mark/3D/kiss_mark_3d.png` },
  { id: 'chocolate', name: 'Chocolate', coins: 30,   emoji: '🍫', img: `${FLUENT}/Chocolate bar/3D/chocolate_bar_3d.png` },
  { id: 'teddy',     name: 'Osito',     coins: 50,   emoji: '🧸', img: `${FLUENT}/Teddy bear/3D/teddy_bear_3d.png` },
  { id: 'gift',      name: 'Regalo',    coins: 75,   emoji: '🎁', img: `${FLUENT}/Wrapped gift/3D/wrapped_gift_3d.png` },
  { id: 'champagne', name: 'Champagne', coins: 100,  emoji: '🍾', img: `${FLUENT}/Bottle with popping cork/3D/bottle_with_popping_cork_3d.png` },
  { id: 'diamond',   name: 'Diamante',  coins: 150,  emoji: '💎', img: `${FLUENT}/Gem stone/3D/gem_stone_3d.png` },
  { id: 'crown',     name: 'Corona',    coins: 200,  emoji: '👑', img: `${FLUENT}/Crown/3D/crown_3d.png` },
  { id: 'unicorn',   name: 'Unicornio', coins: 500,  emoji: '🦄', img: `${FLUENT}/Unicorn/3D/unicorn_3d.png` },
  { id: 'rocket',    name: 'Cohete',    coins: 1000, emoji: '🚀', img: `${FLUENT}/Rocket/3D/rocket_3d.png` },
  { id: 'money',     name: 'Millones',  coins: 2000, emoji: '💸', img: `${FLUENT}/Money with wings/3D/money_with_wings_3d.png` },
];

// ── Tarjeta de regalo ─────────────────────────────────────────────────────────
const GiftCard = ({ gift, userCoins, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const affordable = userCoins >= gift.coins;
  const deficit = gift.coins - userCoins;

  return (
    <button
      onClick={() => affordable && onClick(gift)}
      title={affordable ? `Enviar ${gift.name} — ${gift.coins} coins` : `Te faltan ${deficit} coins`}
      style={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '8px 4px',
        borderRadius: '8px',
        border: '2px solid transparent',
        backgroundColor: 'transparent',
        cursor: affordable ? 'pointer' : 'not-allowed',
        opacity: affordable ? 1 : 0.45,
        transition: 'background-color 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => { if (affordable) e.currentTarget.style.backgroundColor = 'rgba(244,63,94,0.07)'; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* Emoji */}
      <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!imgError ? (
          <img
            src={gift.img}
            alt={gift.name}
            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span style={{ fontSize: '28px', lineHeight: 1 }}>{gift.emoji}</span>
        )}
      </div>

      {/* Nombre */}
      <span style={{
        fontSize: '11px',
        fontWeight: 500,
        marginTop: '4px',
        textAlign: 'center',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {gift.name}
      </span>

      {/* Precio */}
      <span style={{
        fontSize: '11px',
        fontWeight: 700,
        color: affordable ? '#f59e0b' : '#ef4444',
        marginTop: '1px',
      }}>
        {gift.coins} 🪙
      </span>

      {/* Déficit */}
      {!affordable && (
        <span style={{
          fontSize: '9px',
          color: '#ef4444',
          marginTop: '1px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}>
          -{deficit} coins
        </span>
      )}
    </button>
  );
};

// ── Pantalla de confirmación ──────────────────────────────────────────────────
const ConfirmScreen = ({ gift, userCoins, onConfirm, onBack, loading, success, error }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
        {!imgError ? (
          <img
            src={gift.img}
            alt={gift.name}
            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span style={{ fontSize: '64px', lineHeight: 1 }}>{gift.emoji}</span>
        )}
      </div>

      <p className="text-gray-900 dark:text-white font-semibold text-base mb-1">
        ¿Enviar <span className="text-rose-500">{gift.name}</span>?
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
        Costo: <span className="font-bold text-yellow-500">{gift.coins} coins</span>
      </p>
      <p className="text-gray-400 dark:text-gray-500 text-xs mb-4">
        Saldo restante: <span className="font-medium">{Math.max(0, userCoins - gift.coins)} coins</span>
      </p>

      {error && (
        <div className="text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-3">
          {error}
        </div>
      )}
      {success && (
        <div className="text-green-600 dark:text-green-400 text-sm p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mb-3 flex items-center justify-center gap-2">
          <span className="text-xl">{gift.emoji}</span>
          <span>¡Regalo enviado!</span>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center justify-center gap-1"
          onClick={onBack}
          disabled={loading || success}
        >
          <FaArrowLeft size={11} /> Volver
        </button>
        <button
          type="button"
          className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          onClick={onConfirm}
          disabled={loading || success}
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            `Confirmar ${gift.emoji}`
          )}
        </button>
      </div>
    </div>
  );
};

// ── Modal principal ───────────────────────────────────────────────────────────
const GiftModal = ({ isOpen, onClose, userCoins, recipientId, chatId, onSuccess, authToken }) => {
  const [confirming, setConfirming] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const cheapestGift = GIFTS[0].coins; // 1 coin
  const hasNoCoins = userCoins === 0;
  const canAffordAny = userCoins >= cheapestGift;

  const handleSelectGift = (gift) => {
    setError(null);
    setConfirming(gift);
  };

  const handleConfirm = async () => {
    if (!confirming) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/gift-coins`, {
        recipient_id: recipientId,
        chat_id: chatId,
        coins: confirming.coins,
        gift_emoji: confirming.emoji,
        gift_name: confirming.name,
      }, authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined);
      if (onSuccess) onSuccess(confirming);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar el regalo.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setConfirming(null);
    setError(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirming(null);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10020]">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-md w-full mx-4 sm:mx-6">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {confirming ? 'Confirmar regalo' : '🎁 Enviar un regalo'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <FaTimes className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {confirming ? (
          <ConfirmScreen
            gift={confirming}
            userCoins={userCoins}
            onConfirm={handleConfirm}
            onBack={handleBack}
            loading={loading}
            success={success}
            error={error}
          />
        ) : (
          <>
            {/* Balance */}
            <div className="flex items-center gap-2 text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-3">
              <FaCoins className="text-yellow-500" />
              <span className="font-medium">Tu saldo:</span>
              <span className="font-bold">{userCoins} coins</span>
            </div>

            {/* Banner: sin coins */}
            {hasNoCoins && (
              <div className="flex flex-col gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <FaExclamationTriangle size={14} />
                  <span className="font-semibold text-sm">No tienes coins disponibles</span>
                </div>
                <p className="text-red-500 dark:text-red-400 text-xs leading-relaxed">
                  Necesitas coins para enviar regalos. Recarga tu saldo y podrás sorprender a tus modelos favoritas.
                </p>
                <Link
                  to="/client/rechargeCoins"
                  onClick={onClose}
                  className="self-start text-xs font-semibold text-white bg-rose-500 hover:bg-rose-600 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Recargar coins →
                </Link>
              </div>
            )}

            {/* Banner: saldo bajo (puede comprar algo pero no todo) */}
            {!hasNoCoins && !canAffordAny && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
                <FaExclamationTriangle size={13} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-yellow-700 dark:text-yellow-400 font-semibold text-sm">Saldo insuficiente</p>
                  <p className="text-yellow-600 dark:text-yellow-500 text-xs mt-0.5">
                    Con {userCoins} coins no alcanza para el regalo más barato ({cheapestGift} coins).{' '}
                    <Link to="/client/rechargeCoins" onClick={onClose} className="underline font-semibold">
                      Recargar coins
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {/* Nota: precio en rojo = no alcanza */}
            {!hasNoCoins && canAffordAny && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                Los regalos en <span className="text-red-400 font-semibold">rojo</span> requieren más coins de los que tienes.
              </p>
            )}

            {/* Gift grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: '4px',
                maxHeight: '260px',
                overflowY: 'auto',
              }}
            >
              {GIFTS.map((gift) => (
                <GiftCard
                  key={gift.id}
                  gift={gift}
                  userCoins={userCoins}
                  onClick={handleSelectGift}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GiftModal;
